import React, { JSX } from 'react';
import { EORA_SPACES, EoraSpace, IGutterAsMarginProps, useGutterAsMarginCss } from '../../fundamentals/spaces/space';
import styled, { css, useTheme } from 'styled-components';
import { HelpText } from '../HelpText/HelpText';
import { P } from '../Texts/Texts';
import { PassedFocusCssProps, usePassedFocusCss } from '../../fundamentals/actions';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { useCenteredFlexCss } from '../../fundamentals/display';
import { generateOptionColours } from '../../fundamentals/colours/generateUiColours';
import { IColoursCollection } from '../../fundamentals/colours/colourInterfaces';
import { BaseSpan, BaseDiv, BaseInput } from '../../common/BaseElements';
import { PX_TO_REM, PxToRems, SIZES } from '../../fundamentals/sizes/sizes';
import { usePanelBorderRadiusCss } from '../../fundamentals/sizes/borderRadius';

export enum CheckState {
  UNCHECKED = 'unchecked',
  CHECKED = 'checked',
  DISABLED = 'disabled',
  INDETERMINATE = 'indeterminate'
}

export enum CheckColour {
  DEFAULT = 'default',
  SUCCESS = 'success',
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error'
}

export enum OptionType {
  SWITCH = 'switch',
  CHECKBOX = 'checkbox'
}

// Shared between checkboxes, radios, and switches
export const optionColours = (collection: IColoursCollection) => ({
  optionColours: generateOptionColours(collection)
});

export type OptionSize = typeof SIZES.SMALL | typeof SIZES.X_SMALL | typeof SIZES.MICRO;

export interface IOptionProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'>, IGutterAsMarginProps {
  colour?: CheckColour;
  size?: OptionSize;
  label?: string;
  wrapped?: boolean;
  helpText?: JSX.Element | JSX.Element[];
  helpTextAsError?: boolean;
  defaultChecked?: boolean; // The default checked state. Use when the component is not controlled.
  rightAligned?: boolean;
}

export const optionMarginTop: Record<OptionSize, EoraSpace | undefined> = {
  [SIZES.SMALL]: undefined,
  [SIZES.X_SMALL]: EORA_SPACES.XSMALL,
  [SIZES.MICRO]: EORA_SPACES.SMALL
};

export interface IOptionCssProps extends Omit<IOptionProps, 'size'> {
  focused?: boolean;
  optionSize: OptionSize; // renaming size to optionSize to avoid clash with native `size` input param
  optionType?: OptionType;
  hasHelpText?: boolean;
}

export const getOptionBorderSizeCss = (disabled?: boolean, colour?: CheckColour) => {
  const theme = useTheme();
  if (colour && colour !== CheckColour.DEFAULT) {
    const colourSpecificOptions = generateOptionColours(theme.colours, undefined, colour);
    return colourSpecificOptions[disabled ? CheckState.DISABLED : CheckState.UNCHECKED].border?.split(' ')[0] ?? '';
  }
  return theme.optionColours[disabled ? CheckState.DISABLED : CheckState.UNCHECKED].border?.split(' ')[0] ?? '';
};

const useOptionSizeCss = css<Pick<IOptionCssProps, 'optionSize' | 'optionType' | 'disabled'>>`
  height: ${({ theme, optionSize }) => theme.sizes[optionSize!]};

  ${({ theme, optionSize, optionType }) => {
    const width =
      optionType === OptionType.SWITCH
        ? `calc(${theme.sizes[optionSize!]} * 2 - ${theme.eoraSpaces.offgrid_1} - ${theme.eoraSpaces.offgrid_1})`
        : theme.sizes[optionSize!];

    return css`
      width: ${width};
      min-width: ${width};
    `;
  }}
`;

const OptionTextSizes: Record<OptionSize, PxToRems[keyof PxToRems]> = {
  [SIZES.SMALL]: PX_TO_REM[18],
  [SIZES.X_SMALL]: PX_TO_REM[16],
  [SIZES.MICRO]: PX_TO_REM[14]
};

const useCheckboxSizeCss = css<Pick<IOptionCssProps, 'optionSize' | 'optionType' | 'disabled'>>`
  height: ${({ optionSize }) => OptionTextSizes[optionSize!]};
  width: ${({ optionSize }) => OptionTextSizes[optionSize!]};
  min-width: ${({ optionSize }) => OptionTextSizes[optionSize!]};
`;

export const useSwitchBaseStateCss = (selector: CheckState.UNCHECKED | CheckState.CHECKED | CheckState.DISABLED, colour?: CheckColour) => css<IOptionCssProps>`
  background: ${({ theme, optionType }) => {
    let option;
    option = colour && colour !== CheckColour.DEFAULT ? generateOptionColours(theme.colours, undefined, colour) : theme.optionColours;

    const base = option[selector];
    const switchBase = optionType === OptionType.SWITCH && base[optionType];
    return (switchBase || base).background;
  }};
  border: ${({ theme, optionType }) => {
    let option;
    option = colour && colour !== CheckColour.DEFAULT ? generateOptionColours(theme.colours, undefined, colour) : theme.optionColours;

    const base = option[selector];
    const switchBase = optionType === OptionType.SWITCH && base[optionType];
    return (switchBase || base).border;
  }};
  padding: ${({ theme, optionType }) => (optionType === OptionType.SWITCH ? `${theme.eoraSpaces.offgrid_1}` : '')};
`;

const shouldGetTopMargin = (optionType: OptionType | undefined, optionSize: OptionSize) =>
  optionType === OptionType.CHECKBOX && optionSize === SIZES.MICRO ? true : optionType !== OptionType.CHECKBOX;

const useOptionInputGapCss = css<{ optionSize: OptionSize; optionType?: OptionType }>`
  ${({ optionSize, theme, optionType }) =>
    shouldGetTopMargin(optionType, optionSize) &&
    css`
      margin-top: ${theme.eoraSpaces[optionMarginTop[optionSize!]!]};
    `};
`;

const useInputStateCss = css<IOptionCssProps & { colour?: CheckColour }>`
  ${({ colour }) => useSwitchBaseStateCss(CheckState.UNCHECKED, colour)};
  &:checked {
    ${({ colour }) => useSwitchBaseStateCss(CheckState.CHECKED, colour)};
  }
  &:disabled {
    ${({ colour }) => useSwitchBaseStateCss(CheckState.DISABLED, colour)}
  }
`;

const useOptionResetCss = css`
  position: relative;
  margin-left: 0;
  margin-right: 0;
  appearance: none;

  &:not(:disabled) {
    cursor: pointer;
  }
  &:after {
    content: '';
    display: block;
  }
`;

export const useOptionAsOwnInputCss = css<IOptionCssProps>`
  ${useOptionResetCss};
  ${useOptionSizeCss};
  ${useOptionInputGapCss};
  ${useInputStateCss};

  border-radius: ${({ theme, optionSize }) => theme.sizes[optionSize!]};
`;

export const CheckboxSpanBase = styled(BaseSpan)<IOptionProps & IOptionCssProps & PassedFocusCssProps>`
  ${useCenteredFlexCss};
  ${useCheckboxSizeCss};
  ${useOptionInputGapCss};

  position: relative;

  ${({ wrapped }) =>
    !wrapped &&
    css`
      ${usePassedFocusCss};
    `};
`;

const optionGutters: Record<OptionSize, EoraSpace> = {
  [SIZES.SMALL]: EORA_SPACES.LARGE,
  [SIZES.X_SMALL]: EORA_SPACES.MEDIUM,
  [SIZES.MICRO]: EORA_SPACES.MEDIUM
};

const optionGaps: Record<OptionSize, EoraSpace> = {
  [SIZES.SMALL]: EORA_SPACES.LARGE,
  [SIZES.X_SMALL]: EORA_SPACES.MEDIUM,
  [SIZES.MICRO]: EORA_SPACES.MEDIUM
};

const OptionLabel = styled(P)<Pick<IOptionProps, 'disabled' | 'size'>>`
  ${({ theme, disabled }) =>
    !!disabled &&
    css`
      color: ${theme.colours.text.disabled};
    `};
  ${({ size }) => css`
    font-size: ${OptionTextSizes[size!]};
  `};
  padding: 0;
`;

export const OptionsContainer = styled(BaseDiv)<{ compact?: boolean }>`
  display: flex;
  gap: ${({ compact, theme }) => (compact ? theme.eoraSpaces.x2large : theme.eoraSpaces.x8large)};

  & > * {
    width: 50%;
  }
`;

export type OptionWrapperProps = IGutterAsMarginProps &
  Pick<IOptionProps, 'size' | 'wrapped' | 'disabled' | 'rightAligned'> & { focused?: boolean } & PassedFocusCssProps;
export const OptionWrapper = styled(BaseDiv)<OptionWrapperProps>`
  display: flex;
  align-items: center;

  ${({ rightAligned }) =>
    rightAligned &&
    css`
      justify-content: right;
    `}

  ${({ size }) => useGutterAsMarginCss({ bottom: optionGutters[size!] })}
  ${({ wrapped, theme }) =>
    wrapped &&
    css`
      background: ${theme.optionColours.wrapper.default.background};
      ${usePassedFocusCss};
      ${usePanelBorderRadiusCss()};
    `};

  ${({ wrapped, theme, disabled }) =>
    wrapped &&
    !disabled &&
    css`
      cursor: pointer;

      &:has(input:checked) {
        background: ${theme.optionColours.wrapper.defaultSelected.background};
      }

      &:hover {
        background: ${theme.optionColours.wrapper.hover.background};
      }

      &:active {
        background: ${theme.optionColours.wrapper.active.background};
      }
    `};

  ${({ wrapped, theme, disabled }) =>
    wrapped &&
    disabled &&
    css`
      background: ${theme.optionColours.wrapper.disabled.background};
    `};
`;

export const OptionLabelContainer = styled(BaseDiv)<Pick<IOptionProps, 'size' | 'rightAligned'>>`
  ${({ size, theme, rightAligned }) =>
    rightAligned
      ? css`
          margin-right: ${theme.eoraSpaces[optionGaps[size!]]};
          text-align: right;
        `
      : css`
          margin-left: ${theme.eoraSpaces[optionGaps[size!]]};
        `};
`;

export const OptionBase = styled(BaseInput)<IOptionProps>`
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  margin: 0;
  padding: 0;
  z-index: 1;
  cursor: ${({ disabled }) => (disabled ? `inherit` : `pointer`)};
`;

export const OptionTexts: React.FC<
  Pick<IOptionProps, 'label' | 'disabled' | 'helpText' | 'helpTextAsError' | 'size' | 'value' | 'rightAligned'> & { onClick?: React.MouseEventHandler }
> = ({ label, helpText, helpTextAsError, disabled, size, onClick, rightAligned, ...rest }) => {
  if (!label && !helpText) {
    return <></>;
  }

  const { generateTestId } = useTestId(rest, 'OptionText');

  return (
    <OptionLabelContainer size={size} rightAligned={rightAligned} {...generateTestId('OptionText')}>
      {!!label && (
        <OptionLabel
          noTextDecoration
          gutterBottom={EORA_SPACES.SMALL}
          disabled={disabled}
          onClick={disabled ? undefined : onClick}
          size={size}
          {...generateTestId(`Label`)}
        >
          {label}
        </OptionLabel>
      )}
      {!!helpText && (
        <HelpText textEoraSize='SMALL' textStyle='REGULAR' invalid={helpTextAsError} disabled={disabled} gutterBottom={false} {...generateTestId('HelpText')}>
          {helpText}
        </HelpText>
      )}
    </OptionLabelContainer>
  );
};
